import { BrandAccess } from '../enums/brands.js';

/**
 * Prompt template variable definitions and interpolation engine.
 *
 * Template variables use the format `{{namespace.field}}` and are replaced
 * at runtime with contextual values from the agent execution environment.
 *
 * Security: Single-pass replacement only — output of one variable expansion
 * is never re-processed for further `{{...}}` patterns.
 */

export type PromptTemplateVariable = {
  key: string;
  label: string;
  description: string;
  example: string;
  supportedContexts?: TemplateVariableContext[];
};

export type TemplateVariableContext = 'prompt' | 'sql_template';

const DEFAULT_SUPPORTED_CONTEXTS: readonly TemplateVariableContext[] = ['prompt', 'sql_template'];

const USER_METADATA_DYNAMIC_TEMPLATE_KEY = 'user.metadata.[prop_name]';

/**
 * All supported prompt template variables with their metadata.
 * Used by the UI to display available variables and by the interpolation engine.
 */
export const PROMPT_TEMPLATE_VARIABLES: PromptTemplateVariable[] = [
  {
    key: 'account.id',
    label: 'Account ID',
    description: `The unique identifier (UUID) of the user's account.`,
    example: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
  },
  {
    key: 'account.name',
    label: 'Account Name',
    description: `The display name of the user's account.`,
    example: 'Acme Corp',
  },
  {
    key: 'user.id',
    label: 'User ID',
    description: 'The unique identifier (UUID) of the current user.',
    example: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
  },
  {
    key: 'user.name',
    label: 'User Name',
    description: 'The full name of the current user (first + last name).',
    example: 'Jane Smith',
  },
  {
    key: 'user.email',
    label: 'User Email',
    description: 'The email address of the current user.',
    example: 'jane.smith@example.com',
  },
  {
    key: USER_METADATA_DYNAMIC_TEMPLATE_KEY,
    label: 'User Metadata Value',
    description:
      'A dynamic user metadata value by property name. Supports both {{user.metadata.prop_name}} and {{user.metadata.[prop_name]}} syntax.',
    example: '{{user.metadata.department}}',
  },
  {
    key: 'brand.id',
    label: 'Brand ID',
    description: 'The unique identifier of the current brand.',
    example: 'brand-abc123',
  },
  {
    key: 'brand.name',
    label: 'Brand Name',
    description: 'The display name of the current brand.',
    example: 'PharmaCo Brand X',
  },
  {
    key: 'brand.isNation',
    label: 'Brand Is National Access',
    description: 'Whether the current brand access scope is national.',
    example: 'true',
  },
  {
    key: 'brand.imperatives',
    label: 'Brand Strategic Imperatives',
    description: 'The serialized strategic imperatives configured for the brand.',
    example: '<strategic_imperatives>...</strategic_imperatives>',
    supportedContexts: ['prompt'],
  },
  {
    key: 'subject.id',
    label: 'Subject ID',
    description: 'The identifier of the chat context subject (if set).',
    example: '12345',
  },
  {
    key: 'subject.name',
    label: 'Subject Name',
    description: 'The display name of the chat context subject (if set).',
    example: 'Dr. John Doe',
  },
  {
    key: 'subject.kind',
    label: 'Subject Kind',
    description: 'The type/kind of the chat context subject (if set).',
    example: 'hcp',
  },
];

export function isPromptTemplateVariableSupportedInContext(data: {
  variable: PromptTemplateVariable;
  context: TemplateVariableContext;
}): boolean {
  const supportedContexts = data.variable.supportedContexts ?? DEFAULT_SUPPORTED_CONTEXTS;
  return supportedContexts.includes(data.context);
}

export function getPromptTemplateVariablesForContext(
  context: TemplateVariableContext
): PromptTemplateVariable[] {
  return PROMPT_TEMPLATE_VARIABLES.filter((variable) =>
    isPromptTemplateVariableSupportedInContext({ variable, context })
  );
}

/** All known variable keys for fast lookup */
const KNOWN_KEYS = new Set(
  PROMPT_TEMPLATE_VARIABLES.map((variable) => variable.key).filter(
    (variableKey) => variableKey !== USER_METADATA_DYNAMIC_TEMPLATE_KEY
  )
);

/**
 * Context values available for template interpolation at runtime.
 * All values are nullable — if a value is null/undefined, the placeholder is left as-is.
 */
export type PromptTemplateContext = {
  'account.id': string | null;
  'account.name': string | null;
  'user.id': string | null;
  'user.name': string | null;
  'user.email': string | null;
  'user.metadata': Record<string, unknown>;
  'brand.id': string | null;
  'brand.name': string | null;
  'brand.isNation': string | null;
  'brand.imperatives': string | null;
  'subject.id': string | null;
  'subject.name': string | null;
  'subject.kind': string | null;
};

export type BuildPromptTemplateContextParams = {
  user: {
    identifier: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    metadata?: Record<string, unknown>;
    group: {
      identifier: string;
      name: string;
    };
  };
  brand: {
    identifier: string;
    name: string;
    access?: BrandAccess;
  };
  subject: {
    id: number | string;
    name: string;
    kind?: string;
  } | null;
  brandImperatives: string | null;
};

/**
 * Build the template context from runtime agent execution data.
 */
export function buildPromptTemplateContext(params: BuildPromptTemplateContextParams): PromptTemplateContext {
  const fullName = [params.user.firstName, params.user.lastName].filter(Boolean).join(' ') || null;
  let brandIsNation: string | null = null;

  if (params.brand.access != null) {
    brandIsNation = params.brand.access === BrandAccess.National ? 'true' : 'false';
  }

  return {
    'account.id': params.user.group.identifier,
    'account.name': params.user.group.name,
    'user.id': params.user.identifier,
    'user.name': fullName,
    'user.email': params.user.email,
    'user.metadata': params.user.metadata ?? {},
    'brand.id': params.brand.identifier,
    'brand.name': params.brand.name,
    'brand.isNation': brandIsNation,
    'brand.imperatives': params.brandImperatives,
    'subject.id': params.subject != null ? String(params.subject.id) : null,
    'subject.name': params.subject?.name ?? null,
    'subject.kind': params.subject?.kind ?? null,
  };
}

/**
 * Pattern matches `{{namespace.field}}` with optional whitespace inside braces.
 * Matches any non-brace key content; replacement safety is enforced later
 * by known-key checks and metadata key parsing.
 */
const TEMPLATE_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;

function parseUserMetadataKey(key: string): string | null {
  const prefix = 'user.metadata.';
  if (!key.startsWith(prefix)) {
    return null;
  }

  const raw = key.slice(prefix.length).trim();
  if (raw.length === 0) {
    return null;
  }

  if (raw.startsWith('[') && raw.endsWith(']')) {
    const inner = raw.slice(1, -1).trim();
    return inner.length > 0 ? inner : null;
  }

  return raw;
}

export function resolvePromptTemplateVariableValue(
  context: PromptTemplateContext,
  key: string
): string | null {
  const metadataKey = parseUserMetadataKey(key);
  if (metadataKey !== null) {
    const metadataValue = context['user.metadata'][metadataKey];
    return stringifyTemplateValue(metadataValue);
  }

  if (!KNOWN_KEYS.has(key)) {
    return null;
  }

  const value = context[key as keyof PromptTemplateContext];
  return typeof value === 'string' ? value : null;
}

function stringifyTemplateValue(value: unknown): string | null {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }

  try {
    const json = JSON.stringify(value);
    if (json != null) {
      return json;
    }
  } catch {
    // Fall through to string coercion for non-JSON-serializable values.
  }

  return String(value);
}

/**
 * Interpolate template variables in a prompt string.
 *
 * - Single-pass replacement only (no recursive expansion).
 * - Only known variable keys are replaced; unknown `{{foo.bar}}` are left as-is.
 * - Null/undefined context values leave the placeholder untouched.
 */
export function interpolatePromptTemplate(template: string, context: PromptTemplateContext): string {
  return template.replace(TEMPLATE_PATTERN, (match, key: string) => {
    const renderedValue = resolvePromptTemplateVariableValue(context, key);
    if (renderedValue == null) {
      return match;
    }
    return renderedValue;
  });
}
